/**
 * EIA fuel-code → display label + `.rd`-palette color, covering both the
 * consequential marginal codes (COL/NG/OIL only — `_MARGINAL_FUEL_TO_EIA`) and
 * the attributional average-mix codes (+ NUC/SUN/WND/GEO/OTH and the hydro
 * subtypes WAT_ROR/WAT_RES/WAT_PS — `_AVG_BASE_FUELS`) emitted by the model's
 * dispatch/orchestrator into `grid_supply.meta.per_fuel_mwh`.
 *
 * The hydro subtypes fold to a single "Hydro" bucket so the mix bar stays
 * legible. Colors are the earthy/instrument tones the Range dashboard already
 * uses for fuels, extended for the clean fuels that only appear on the
 * attributional (full-mix) side.
 */

export interface FuelMeta {
  label: string;
  color: string;
  /** true = fossil / combustion (the consequential margin). */
  fossil: boolean;
}

const FUELS: Record<string, FuelMeta> = {
  COL: { label: "Coal", color: "#5b5b5b", fossil: true },
  NG: { label: "Natural gas", color: "#8a6d3b", fossil: true },
  OIL: { label: "Oil", color: "#b0642f", fossil: true },
  NUC: { label: "Nuclear", color: "#8a7bbf", fossil: false },
  SUN: { label: "Solar", color: "#d9a441", fossil: false },
  WND: { label: "Wind", color: "#5aa9a3", fossil: false },
  GEO: { label: "Geothermal", color: "#a2593f", fossil: false },
  WAT: { label: "Hydro", color: "#3f7fb0", fossil: false },
  WAT_ROR: { label: "Hydro", color: "#3f7fb0", fossil: false },
  WAT_RES: { label: "Hydro", color: "#3f7fb0", fossil: false },
  WAT_PS: { label: "Hydro", color: "#4a8fc0", fossil: false },
  OTH: { label: "Other", color: "#9aa6ab", fossil: false },
};

const FALLBACK: FuelMeta = { label: "Other", color: "#9aa6ab", fossil: false };

export function fuelMeta(code: string): FuelMeta {
  return FUELS[code] ?? { ...FALLBACK, label: code };
}

export interface FuelSlice {
  code: string;
  label: string;
  color: string;
  fossil: boolean;
  mwh: number;
  pct: number;
}

/**
 * Roll a `per_fuel_mwh` map into display slices: hydro subtypes are merged into
 * one "Hydro" slice, zero/negative entries dropped, sorted by MWh descending,
 * percentages computed against the served total.
 */
export function fuelSlices(perFuel: Record<string, number> | undefined): FuelSlice[] {
  if (!perFuel) return [];
  // Merge by display label so the three hydro subtypes collapse into one slice.
  const byLabel = new Map<string, FuelSlice>();
  for (const [code, mwh] of Object.entries(perFuel)) {
    if (!(mwh > 0)) continue;
    const m = fuelMeta(code);
    const existing = byLabel.get(m.label);
    if (existing) {
      existing.mwh += mwh;
    } else {
      byLabel.set(m.label, {
        code,
        label: m.label,
        color: m.color,
        fossil: m.fossil,
        mwh,
        pct: 0,
      });
    }
  }
  const slices = [...byLabel.values()];
  const total = slices.reduce((s, x) => s + x.mwh, 0) || 1;
  for (const s of slices) s.pct = (s.mwh / total) * 100;
  return slices.sort((a, b) => b.mwh - a.mwh);
}

export function fmtMWh(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e6) return `${(a / 1e6).toFixed(2)}M MWh`;
  if (a >= 1e3) return `${(a / 1e3).toFixed(0)}K MWh`;
  return `${a.toFixed(0)} MWh`;
}
